import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { submitCareerToSupabase } from '../lib/supabase';
import {
  Briefcase,
  Upload,
  FileText,
  CheckCircle2,
  AlertCircle,
  Globe,
  Clock,
  Sparkles,
  ChevronDown,
  ChevronUp,
  DollarSign,
  User,
  Mail,
  Phone,
  MapPin,
  Link as LinkIcon,
  Send,
  X,
  CreditCard,
  TrendingUp,
  Award,
  ArrowRight,
  ShieldCheck,
  Building,
  Check,
  Copy,
  MessageSquare,
  Laptop,
  Wifi,
  Zap,
  BarChart3,
  Users,
  Code,
  BookOpen,
  Palette,
  Layers,
  PieChart,
  FolderCheck
} from 'lucide-react';
import { CAREER_ROLES, FREELANCE_BENEFITS, CATEGORY_APPLICATION_STATS, CategoryAppStat } from '../data/careersData';
import { COMPANY_INFO } from '../data/companyData';
import { CareerRole, CareerApplicationData } from '../types';
import { useLanguage } from '../context/LanguageContext';

interface CareersPageProps {
  onBackToHome?: () => void;
  onOpenContact?: () => void;
}

const AVAILABLE_SKILLS = [
  'Microsoft Office (Word / Excel)',
  'Adobe InDesign CC',
  'Talent Acquisition & HR',
  'Statutory Labour Compliance',
  'B2B Sales & Business Development',
  'Publishing Operations & SLAs',
  'Accounts & GST / TDS Filing',
  'Hyphenation & H&J Control',
  'Widow & Orphan Elimination',
  'Baseline Grid & Optical Alignment',
  'Adobe Illustrator & Graphics',
  'XML / JATS / BITS Conversion',
  'EPUB 3.2 Reflow & Fixed Layout',
  'PDF/UA & WCAG Accessibility',
  'STEM Alt-Text Writing',
  'MathType & LaTeX Formatting',
  'APA / Chicago Copyediting',
  'Prepress & Print Production'
];

export const CareersPage: React.FC<CareersPageProps> = ({ onBackToHome, onOpenContact }) => {
  const { t } = useLanguage();
  const [vacancies, setVacancies] = useState<CareerRole[]>(CAREER_ROLES);
  const [isLoadingVacancies, setIsLoadingVacancies] = useState<boolean>(true);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    // Fetch live CMS vacancies from backend
    fetch('/api/vacancies')
      .then((res) => res.json())
      .then((data) => {
        if (data.success && Array.isArray(data.vacancies) && data.vacancies.length > 0) {
          // Normalize backend vacancies format to match CareerRole
          const mapped: CareerRole[] = data.vacancies.map((v: any) => {
            const resp = Array.isArray(v.responsibilities) && v.responsibilities.length > 0
              ? v.responsibilities
              : (Array.isArray(v.keyResponsibilities) ? v.keyResponsibilities : ['Perform assigned typesetting/publishing tasks accurately.']);

            const reqs = Array.isArray(v.requirements) && v.requirements.length > 0
              ? v.requirements
              : (Array.isArray(v.requiredSkills) ? v.requiredSkills : ['Publishing / Prepress Knowledge']);

            const comp = v.showSalary && v.salaryRange ? v.salaryRange : (v.compensation || 'Competitive Contract Rates');

            return {
              id: v.id,
              title: v.title,
              department: v.department || 'General',
              type: v.type || 'Freelance',
              experience: v.experienceLevel || v.experience || '1+ Years',
              experienceLevel: v.experienceLevel,
              location: v.location || '100% Remote (Global)',
              summary: v.shortDescription || v.summary || v.fullDescription || 'Join ASAP LEARNING as a publishing specialist.',
              shortDescription: v.shortDescription,
              fullDescription: v.fullDescription,
              keyResponsibilities: resp,
              responsibilities: resp,
              requiredSkills: reqs,
              requirements: reqs,
              compensation: comp,
              salaryRange: v.salaryRange,
              status: v.status || 'Live',
              deadline: v.deadline,
              badge: v.badge || 'Active Role',
              urgent: v.badge?.toLowerCase().includes('urgent') || v.badge?.toLowerCase().includes('immediate') || false
            };
          });
          setVacancies(mapped);
          setExpandedRoleId(mapped[0]?.id || null);
          setSelectedRoleForApp(mapped[0]?.title || 'General Position');
        }
      })
      .catch((err) => {
        console.warn('Backend vacancies fetch notice:', err);
      })
      .finally(() => {
        setIsLoadingVacancies(false);
      });
  }, []);

  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const [expandedRoleId, setExpandedRoleId] = useState<string | null>(CAREER_ROLES[0].id);
  const [selectedRoleForApp, setSelectedRoleForApp] = useState<string>(CAREER_ROLES[0].title);
  const [categoryStats, setCategoryStats] = useState<CategoryAppStat[]>(() => {
    try {
      const isReset = localStorage.getItem('asap_is_reset') === 'true';
      if (isReset) {
        return CATEGORY_APPLICATION_STATS.map((c) => ({ ...c, applicationCount: 0 }));
      }
      const saved = localStorage.getItem('asap_category_app_counts');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return CATEGORY_APPLICATION_STATS.map((def) => {
            const match = parsed.find((p: { department: string; applicationCount?: number }) => p.department === def.department);
            return match ? { ...def, applicationCount: match.applicationCount || 0 } : { ...def, applicationCount: 0 };
          });
        }
      }
    } catch (e) {
      console.warn('Failed to parse localStorage app counts:', e);
    }
    return CATEGORY_APPLICATION_STATS.map((c) => ({ ...c, applicationCount: 0 }));
  });

  const totalApplications = categoryStats.reduce((acc, curr) => acc + curr.applicationCount, 0);

  const getCategoryIcon = (iconName: string) => {
    switch (iconName) {
      case 'FileText': return <FileText className="w-5 h-5 text-blue-600" />;
      case 'Globe': return <Globe className="w-5 h-5 text-cyan-600" />;
      case 'Code': return <Code className="w-5 h-5 text-indigo-600" />;
      case 'ShieldCheck': return <ShieldCheck className="w-5 h-5 text-emerald-600" />;
      case 'BookOpen': return <BookOpen className="w-5 h-5 text-purple-600" />;
      case 'Users': return <Users className="w-5 h-5 text-sky-600" />;
      case 'TrendingUp': return <TrendingUp className="w-5 h-5 text-amber-600" />;
      case 'Briefcase': return <Briefcase className="w-5 h-5 text-rose-600" />;
      case 'CreditCard': return <CreditCard className="w-5 h-5 text-teal-600" />;
      case 'Palette': return <Palette className="w-5 h-5 text-orange-600" />;
      case 'Layers': return <Layers className="w-5 h-5 text-slate-700" />;
      default: return <BarChart3 className="w-5 h-5 text-blue-600" />;
    }
  };
  
  // Application Form State
  const [formData, setFormData] = useState<CareerApplicationData>({
    fullName: '',
    email: '',
    phone: '',
    location: '',
    communicationAddress: '',
    aadhaarNumber: '',
    panNumber: '',
    preferredWorkMode: 'Remote',
    appliedRole: CAREER_ROLES[0].title,
    experienceYears: '3-5 years',
    skills: ['Microsoft Office (Word / Excel)', 'Adobe InDesign CC'],
    availability: '20-40 hours / week',
    expectedRate: '',
    ownSystem: 'Windows',
    internetType: 'WIFI Connection',
    powerBackup: 'Yes',
    portfolioUrl: '',
    linkedinUrl: '',
    resumeDriveUrl: '',
    coverLetter: '',
  });

  // Form Submission State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [applicationId, setApplicationId] = useState<string>('');
  const [formError, setFormError] = useState<string | null>(null);
  const [copiedEmail, setCopiedEmail] = useState(false);

  const officialEmail = COMPANY_INFO.careersEmail || COMPANY_INFO.email;

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(officialEmail);
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2500);
  };

  const constructMailtoUrl = () => {
    const subject = encodeURIComponent(`Job Application: ${formData.appliedRole || 'Freelance Role'} - ${formData.fullName || 'Candidate'}`);
    const body = encodeURIComponent(
      `Dear ASAP LEARNING Hiring Team,\n\n` +
      `I would like to apply for the position: ${formData.appliedRole || 'Freelance Position'}.\n\n` +
      `=== APPLICANT DETAILS ===\n` +
      `Full Name: ${formData.fullName || 'Not specified'}\n` +
      `Email Address: ${formData.email || 'Not specified'}\n` +
      `Phone Number: ${formData.phone || 'Not specified'}\n` +
      `Communication Address: ${formData.communicationAddress || 'Not specified'}\n` +
      `Current Location: ${formData.location || 'Not specified'}\n` +
      `Aadhaar Card Number: ${formData.aadhaarNumber || 'Not specified'}\n` +
      `PAN Card Number: ${formData.panNumber || 'Not specified'}\n` +
      `Preferred Work Mode: ${formData.preferredWorkMode || 'Remote'}\n` +
      `Years of Experience: ${formData.experienceYears || 'Not specified'}\n` +
      `Weekly Availability: ${formData.availability || 'Not specified'}\n` +
      `Expected Rate / Pay: ${formData.expectedRate || 'Not specified'}\n` +
      `Selected Skills: ${formData.skills.length > 0 ? formData.skills.join(', ') : 'None'}\n\n` +
      `=== WORKSTATION & INFRASTRUCTURE ===\n` +
      `Own System / Computer: ${formData.ownSystem || 'Windows'}\n` +
      `Internet Connection: ${formData.internetType || 'WIFI Connection'}\n` +
      `Power Backup (UPS/Inverter): ${formData.powerBackup || 'Yes'}\n\n` +
      `=== LINKS & RESUME ===\n` +
      `Resume Drive Link: ${formData.resumeDriveUrl || 'Not provided'}\n` +
      `Portfolio / Drive URL: ${formData.portfolioUrl || 'None'}\n` +
      `LinkedIn URL: ${formData.linkedinUrl || 'None'}\n\n` +
      `=== COVER LETTER / SUMMARY ===\n` +
      `${formData.coverLetter || 'Hi, please review my resume link provided above.'}\n\n` +
      `Best regards,\n${formData.fullName || 'Candidate'}`
    );
    return `mailto:${officialEmail}?subject=${subject}&body=${body}`;
  };

  const constructWhatsappUrl = () => {
    const text = encodeURIComponent(
      `*ASAP LEARNING FREELANCE JOB APPLICATION*\n` +
      `------------------------------------\n` +
      `*Position:* ${formData.appliedRole || 'Freelance Role'}\n` +
      `*Applicant Name:* ${formData.fullName}\n` +
      `*Email:* ${formData.email}\n` +
      `*Phone:* ${formData.phone}\n` +
      `*Address:* ${formData.communicationAddress || 'N/A'}\n` +
      `*Location:* ${formData.location || '100% Remote'}\n` +
      `*Aadhaar Number:* ${formData.aadhaarNumber || 'N/A'}\n` +
      `*PAN Number:* ${formData.panNumber || 'N/A'}\n` +
      `*Work Mode:* ${formData.preferredWorkMode || 'Remote'}\n` +
      `*Experience:* ${formData.experienceYears}\n` +
      `*Availability:* ${formData.availability}\n` +
      `*Expected Rate:* ${formData.expectedRate || 'Negotiable'}\n` +
      `*Skills:* ${formData.skills.join(', ')}\n\n` +
      `*WORKSTATION SETUP:*\n` +
      `• System: ${formData.ownSystem || 'Windows'}\n` +
      `• Internet: ${formData.internetType || 'WIFI Connection'}\n` +
      `• Power Backup: ${formData.powerBackup || 'Yes'}\n\n` +
      `*Resume Drive Link:* ${formData.resumeDriveUrl || 'N/A'}\n` +
      `*Portfolio / LinkedIn:* ${formData.portfolioUrl || formData.linkedinUrl || 'N/A'}\n\n` +
      `*Cover Letter:* ${formData.coverLetter || 'Hi, I would like to join ASAP LEARNING as a freelance specialist.'}`
    );
    return `https://wa.me/917817899559?text=${text}`;
  };

  const formRef = useRef<HTMLDivElement>(null);

  // Department Filters
  const departments = ['all', ...Array.from(new Set(vacancies.map((r) => r.department)))];

  const filteredRoles = selectedDepartment === 'all'
    ? vacancies
    : vacancies.filter((r) => r.department === selectedDepartment);

  const handleRoleSelectForApply = (roleTitle: string) => {
    setSelectedRoleForApp(roleTitle);
    setFormData((prev) => ({ ...prev, appliedRole: roleTitle }));
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleSkill = (skill: string) => {
    setFormData((prev) => {
      const exists = prev.skills.includes(skill);
      if (exists) {
        return { ...prev, skills: prev.skills.filter((s) => s !== skill) };
      } else {
        return { ...prev, skills: [...prev.skills, skill] };
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!formData.fullName.trim() || !formData.email.trim() || !formData.phone.trim() || !formData.location.trim()) {
      setFormError('Please fill in all mandatory contact details (Full Name, Email, Phone, City & Country).');
      return;
    }

    if (!formData.communicationAddress?.trim()) {
      setFormError('Please provide your complete Communication Address.');
      return;
    }

    if (!formData.aadhaarNumber?.trim()) {
      setFormError('Please enter your 12-digit Aadhaar Card Number.');
      return;
    }

    if (!formData.panNumber?.trim()) {
      setFormError('Please enter your 10-character PAN Card Number.');
      return;
    }

    if (!formData.expectedRate.trim()) {
      setFormError('Please enter your Expected Rate / Compensation.');
      return;
    }

    if (!formData.portfolioUrl.trim()) {
      setFormError('Please provide your Portfolio, Google Drive, or Sample Work link.');
      return;
    }

    if (!formData.linkedinUrl.trim()) {
      setFormError('Please provide your LinkedIn profile URL.');
      return;
    }

    if (!formData.skills || formData.skills.length === 0) {
      setFormError('Please select at least one primary software or skill proficiency.');
      return;
    }

    if (!formData.skills.some((s) => s.includes('Microsoft Office'))) {
      setFormError('❌ Microsoft Office (Word / Excel) proficiency is mandatory for all positions at ASAP LEARNING. Please select Microsoft Office skill.');
      return;
    }

    if (!formData.ownSystem) {
      setFormError('Please select your workstation system (Windows / MAC / Both / None).');
      return;
    }

    if (formData.ownSystem === 'None' || formData.ownSystem === 'None (No System)') {
      setFormError('❌ Application Blocked: A personal Computer/System (Windows or MAC) is strictly required to work at ASAP LEARNING. Candidates without a system cannot submit an application.');
      return;
    }

    if (!formData.internetType) {
      setFormError('Please select your internet connection type.');
      return;
    }

    if (formData.internetType === 'No Internet Connection' || formData.internetType === 'None') {
      setFormError('❌ Application Blocked: A stable internet connection is strictly required for remote publishing work. Candidates without internet access cannot submit an application.');
      return;
    }

    if (!formData.powerBackup) {
      setFormError('Please select your power backup status (Yes / No).');
      return;
    }

    if (!formData.resumeDriveUrl.trim()) {
      setFormError('Please paste your Resume / CV Google Drive or Cloud link.');
      return;
    }

    if (!formData.coverLetter.trim()) {
      setFormError('Please write a brief intro or experience summary.');
      return;
    }

    setIsSubmitting(true);

    const applicationPayload = {
      fullName: formData.fullName,
      email: formData.email,
      phone: formData.phone,
      communicationAddress: formData.communicationAddress,
      location: formData.location || '100% Remote',
      aadhaarNumber: formData.aadhaarNumber,
      panNumber: formData.panNumber,
      preferredWorkMode: formData.preferredWorkMode || 'Remote',
      appliedRole: formData.appliedRole,
      experienceYears: formData.experienceYears,
      skills: formData.skills,
      availability: formData.availability,
      expectedRate: formData.expectedRate,
      ownSystem: formData.ownSystem || 'Windows',
      internetType: formData.internetType || 'WIFI Connection',
      powerBackup: formData.powerBackup || 'Yes',
      portfolioUrl: formData.portfolioUrl,
      linkedinUrl: formData.linkedinUrl,
      resumeDriveUrl: formData.resumeDriveUrl,
      coverLetter: formData.coverLetter
    };

    // 1. Save record locally in localStorage
    try {
      const newJobApp = {
        submission_id: `ASAP-CAREER-${Date.now().toString(36).toUpperCase()}`,
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        location: formData.location || '100% Remote',
        communication_address: formData.communicationAddress || '',
        aadhaar_number: formData.aadhaarNumber || '',
        pan_number: formData.panNumber || '',
        preferred_work_mode: formData.preferredWorkMode || 'Remote',
        applied_role: formData.appliedRole,
        experience_years: formData.experienceYears,
        skills: Array.isArray(formData.skills) ? formData.skills.join(', ') : formData.skills,
        availability: formData.availability,
        expected_rate: formData.expectedRate,
        own_system: formData.ownSystem || 'Windows',
        internet_type: formData.internetType || 'WIFI Connection',
        power_backup: formData.powerBackup || 'Yes',
        portfolio_url: formData.portfolioUrl,
        linkedin_url: formData.linkedinUrl,
        resume_drive_url: formData.resumeDriveUrl,
        cover_letter: formData.coverLetter,
        created_at: new Date().toISOString()
      };
      const existingApps = JSON.parse(localStorage.getItem('asap_job_applications') || '[]');
      localStorage.setItem('asap_job_applications', JSON.stringify([newJobApp, ...existingApps]));
    } catch (e) {
      console.warn('LocalStorage job app save warning:', e);
    }

    // 2. Save record to Firebase Firestore & Supabase
    try {
      submitCareerToSupabase(applicationPayload).catch((err) =>
        console.warn('Supabase career client save warning:', err)
      );

      await addDoc(collection(db, 'career_applications'), {
        ...applicationPayload,
        status: 'Submitted',
        createdAt: new Date().toISOString()
      });
    } catch (firebaseErr) {
      console.warn('Firestore saving warning:', firebaseErr);
    }

    // 2. Send to server-side API handler (/api/careers)
    try {
      await fetch('/api/careers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(applicationPayload)
      });
    } catch (err) {
      console.warn('Server API careers dispatch warning:', err);
    }

    // 2. Direct dual-dispatch via FormSubmit AJAX endpoint to contact@asaplearning.in
    try {
      await fetch(`https://formsubmit.co/ajax/${officialEmail}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          _subject: `[JOB APPLICATION] ${formData.appliedRole} - ${formData.fullName}`,
          _replyto: formData.email,
          'Applied Position': formData.appliedRole,
          'Full Name': formData.fullName,
          'Email': formData.email,
          'Phone': formData.phone,
          'Preferred Work Mode': formData.preferredWorkMode || 'Remote',
          'Communication Address': formData.communicationAddress || 'Not Provided',
          'Current Location': formData.location || '100% Remote',
          'Aadhaar Card Number': formData.aadhaarNumber || 'Not Provided',
          'PAN Card Number': formData.panNumber || 'Not Provided',
          'Experience': formData.experienceYears,
          'Skills': formData.skills.join(', '),
          'Availability': formData.availability,
          'Expected Pay': formData.expectedRate || 'Negotiable',
          'Own Workstation System': formData.ownSystem || 'Windows',
          'Internet Connection': formData.internetType || 'WIFI Connection',
          'Power Backup (UPS/Inverter)': formData.powerBackup || 'Yes',
          'Resume Google Drive Link': formData.resumeDriveUrl,
          'Portfolio': formData.portfolioUrl || 'N/A',
          'LinkedIn': formData.linkedinUrl || 'N/A',
          'Cover Letter': formData.coverLetter || 'N/A'
        })
      });
    } catch (err) {
      console.warn('FormSubmit AJAX dispatch warning:', err);
    }

    // 3. Trigger client mail client popup automatically
    try {
      const mailtoUrl = constructMailtoUrl();
      window.open(mailtoUrl, '_blank');
    } catch (err) {
      console.warn('Mailto popup launch error:', err);
    }

    // 4. Increment category application count for live feedback
    const appliedRoleObj = CAREER_ROLES.find((r) => r.title === formData.appliedRole);
    if (appliedRoleObj) {
      setCategoryStats((prev) => {
        const updated = prev.map((stat) => {
          if (
            stat.department === appliedRoleObj.department ||
            stat.department === 'All Departments'
          ) {
            return { ...stat, applicationCount: stat.applicationCount + 1 };
          }
          return stat;
        });
        try {
          localStorage.setItem('asap_category_app_counts', JSON.stringify(updated));
        } catch (e) {
          console.warn('Failed to save app counts to localStorage:', e);
        }
        return updated;
      });
    }

    setIsSubmitting(false);
    setIsSubmitted(true);
    const generatedId = 'ASAP-JOIN-' + Math.floor(100000 + Math.random() * 900000);
    setApplicationId(generatedId);
  };

  const getBenefitIcon = (iconName: string) => {
    switch (iconName) {
      case 'Globe': return <Globe className="w-6 h-6 text-blue-500" />;
      case 'CreditCard': return <CreditCard className="w-6 h-6 text-emerald-500" />;
      case 'TrendingUp': return <TrendingUp className="w-6 h-6 text-purple-500" />;
      case 'Sparkles': return <Sparkles className="w-6 h-6 text-amber-500" />;
      default: return <Award className="w-6 h-6 text-blue-500" />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 pt-4 pb-20">
      
      {/* Careers Hero Header */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 text-white relative overflow-hidden py-16 sm:py-24">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-600/20 via-transparent to-transparent pointer-events-none" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          
          <div className="text-center max-w-3xl mx-auto space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-300 text-xs font-bold uppercase tracking-wider">
              <Briefcase className="w-4 h-4 text-blue-400" /> Freelance & Career Opportunities
            </div>

            <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight leading-tight">
              Work With Us as a <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-sky-300 to-purple-300">Freelance Expert</span>
            </h1>

            <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
              ASAP LEARNING is expanding its network of high-caliber typesetters, XML engineers, accessibility remediators, copyeditors, and vector illustrators. Join our global freelance production engine.
            </p>

            <div className="pt-2 flex flex-wrap justify-center gap-4">
              <a
                href="#open-positions"
                className="px-6 py-3.5 rounded-xl font-bold text-sm bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg shadow-blue-600/25 hover:from-blue-700 hover:to-purple-700 transition-all inline-flex items-center gap-2"
              >
                <span>Browse Open Positions</span>
                <ArrowRight className="w-4 h-4" />
              </a>

              <a
                href="#apply-form"
                onClick={() => {
                  if (formRef.current) formRef.current.scrollIntoView({ behavior: 'smooth' });
                }}
                className="px-6 py-3.5 rounded-xl font-bold text-sm bg-slate-800 text-white border border-slate-700 hover:bg-slate-700 transition-all inline-flex items-center gap-2"
              >
                <Upload className="w-4 h-4 text-blue-400" />
                <span>Upload Resume Directly</span>
              </a>
            </div>
          </div>

          {/* Quick Metrics Bar */}
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4 bg-slate-800/80 backdrop-blur-md rounded-2xl p-6 border border-slate-700/80 shadow-2xl">
            <div className="text-center space-y-1">
              <p className="text-2xl sm:text-3xl font-extrabold text-blue-400">100% Remote</p>
              <p className="text-xs text-slate-300 font-medium">Work From Anywhere</p>
            </div>
            <div className="text-center space-y-1 border-l border-slate-700/80">
              <p className="text-2xl sm:text-3xl font-extrabold text-emerald-400">18+ Publishers</p>
              <p className="text-xs text-slate-300 font-medium">Global Project Pipeline</p>
            </div>
            <div className="text-center space-y-1 border-l border-slate-700/80">
              <p className="text-2xl sm:text-3xl font-extrabold text-purple-400">Prompt Pay</p>
              <p className="text-xs text-slate-300 font-medium">Weekly / Monthly Payouts</p>
            </div>
            <div className="text-center space-y-1 border-l border-slate-700/80">
              <p className="text-2xl sm:text-3xl font-extrabold text-amber-400">AI Powered</p>
              <p className="text-xs text-slate-300 font-medium">Automated Helper Tools</p>
            </div>
          </div>

        </div>
      </section>

      {/* Freelance Perks & Benefits Section */}
      <section className="py-14 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto space-y-2 mb-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
            Why Join the ASAP LEARNING Freelance Network?
          </h2>
          <p className="text-slate-600 text-sm sm:text-base">
            We value craftsmanship, timely communication, and fair compensation.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {FREELANCE_BENEFITS.map((b, idx) => (
            <div
              key={idx}
              className="bg-white rounded-2xl p-6 border border-slate-200/90 shadow-sm hover:shadow-md transition-all space-y-3"
            >
              <div className="p-3 w-12 h-12 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center">
                {getBenefitIcon(b.iconName)}
              </div>
              <h3 className="text-base font-bold text-slate-900">{b.title}</h3>
              <p className="text-xs text-slate-600 leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Open Positions List */}
      <section id="open-positions" className="py-12 bg-white border-y border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="mb-8">
            <div className="space-y-1">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-bold uppercase tracking-wider">
                <Building className="w-3.5 h-3.5" /> Current Openings
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
                Explore Freelance & Contract Roles
              </h2>
            </div>
          </div>

          {/* Role Cards Stack */}
          <div className="space-y-4">
            {filteredRoles.map((role) => {
              const isExpanded = expandedRoleId === role.id;
              const roleStat = categoryStats.find((s) => s.department === role.department);

              return (
                <div
                  key={role.id}
                  className={`rounded-2xl border transition-all duration-300 overflow-hidden ${
                    isExpanded
                      ? 'bg-slate-900 text-white border-slate-800 shadow-xl'
                      : 'bg-white text-slate-900 border-slate-200 hover:border-blue-300 hover:shadow-md'
                  }`}
                >
                  {/* Card Main Bar */}
                  <div
                    onClick={() => setExpandedRoleId(isExpanded ? null : role.id)}
                    className="p-5 sm:p-6 cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                  >
                    <div className="space-y-2 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider ${
                          isExpanded ? 'bg-blue-500/20 text-blue-300 border border-blue-400/30' : 'bg-blue-50 text-blue-700 border border-blue-200'
                        }`}>
                          {role.type}
                        </span>
                        <span className={`text-xs font-medium ${isExpanded ? 'text-slate-400' : 'text-slate-500'}`}>
                          {role.department}
                        </span>
                        {roleStat && (
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border flex items-center gap-1 ${
                            isExpanded ? 'bg-slate-800 text-slate-300 border-slate-700' : 'bg-slate-100 text-slate-700 border-slate-200'
                          }`}>
                            <Users className="w-3 h-3 text-blue-400" />
                            <span>{roleStat.applicationCount} Applications Received</span>
                          </span>
                        )}
                        {role.urgent && (
                          <span className="px-2 py-0.5 rounded-full bg-rose-500/20 border border-rose-500/30 text-rose-300 text-[10px] font-extrabold uppercase tracking-wider animate-pulse">
                            Immediate Hiring
                          </span>
                        )}
                      </div>

                      <h3 className={`text-lg sm:text-xl font-bold ${isExpanded ? 'text-white' : 'text-slate-900'}`}>
                        {role.title}
                      </h3>

                      <p className={`text-xs sm:text-sm line-clamp-2 ${isExpanded ? 'text-slate-300' : 'text-slate-600'}`}>
                        {role.summary}
                      </p>
                    </div>

                    <div className="flex items-center gap-3 shrink-0 pt-2 sm:pt-0">
                      <div className="text-left sm:text-right">
                        <p className={`text-xs font-bold ${isExpanded ? 'text-emerald-400' : 'text-emerald-600'}`}>
                          {role.compensation}
                        </p>
                        <p className={`text-[11px] ${isExpanded ? 'text-slate-400' : 'text-slate-500'}`}>
                          {role.location}
                        </p>
                      </div>

                      <div className={`p-2 rounded-xl transition-colors ${
                        isExpanded ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Accordion Details */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="border-t border-slate-800 p-5 sm:p-6 bg-slate-900/90 space-y-6"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                          {/* Responsibilities */}
                          <div className="space-y-3">
                            <h4 className="text-xs font-bold text-blue-400 uppercase tracking-wider flex items-center gap-1.5">
                              <CheckCircle2 className="w-4 h-4" /> Key Responsibilities
                            </h4>
                            <ul className="space-y-2 text-slate-300 text-xs sm:text-sm">
                              {(role.keyResponsibilities || role.responsibilities || []).map((item, i) => (
                                <li key={i} className="flex items-start gap-2">
                                  <span className="text-blue-400 font-bold mt-0.5">•</span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          {/* Required Skills */}
                          <div className="space-y-3">
                            <h4 className="text-xs font-bold text-purple-400 uppercase tracking-wider flex items-center gap-1.5">
                              <Sparkles className="w-4 h-4" /> Required Skills & Tools
                            </h4>
                            <div className="flex flex-wrap gap-1.5">
                              {(role.requiredSkills || role.requirements || []).map((skill, i) => (
                                <span
                                  key={i}
                                  className="px-2.5 py-1 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 text-xs font-medium"
                                >
                                  {skill}
                                </span>
                              ))}
                            </div>

                            <div className="pt-2 text-xs text-slate-400 space-y-1">
                              <p><strong className="text-slate-200">Required Experience:</strong> {role.experience}</p>
                              <p><strong className="text-slate-200">Location:</strong> {role.location}</p>
                            </div>
                          </div>
                        </div>

                        {/* Apply CTA inside card */}
                        <div className="pt-4 border-t border-slate-800/80 flex flex-col sm:flex-row items-center justify-between gap-4">
                          <p className="text-xs text-slate-400">
                            Interested in this role? Submit your details & resume below.
                          </p>

                          <button
                            onClick={() => handleRoleSelectForApply(role.title)}
                            className="w-full sm:w-auto px-6 py-2.5 rounded-xl font-bold text-xs bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700 transition-all inline-flex items-center justify-center gap-2 shadow-md"
                          >
                            <span>Apply For This Position</span>
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                </div>
              );
            })}
          </div>

        </div>
      </section>

      {/* Application & Resume Upload Form Section */}
      <section ref={formRef} id="apply-form" className="py-16 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="bg-white rounded-3xl p-6 sm:p-10 border border-slate-200 shadow-xl relative">
          
          <div className="border-b border-slate-100 pb-6 mb-8 text-center sm:text-left space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-50 text-purple-700 text-xs font-bold uppercase tracking-wider mb-2">
                  <Upload className="w-3.5 h-3.5" /> Freelancer Registration
                </div>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
                  Apply to Join ASAP LEARNING
                </h2>
                <p className="text-slate-600 text-xs sm:text-sm mt-1">
                  Complete the form below or email your application directly to our official HR address.
                </p>
              </div>

              {/* Direct Email ID Linking Badge */}
              <div className="bg-slate-900 text-white rounded-2xl p-4 border border-slate-800 shadow-lg text-left shrink-0 max-w-xs space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-bold text-blue-400 uppercase tracking-wider flex items-center gap-1">
                    <Mail className="w-3 h-3" /> Official HR Email
                  </span>
                  <button
                    onClick={handleCopyEmail}
                    className="text-[10px] font-bold text-slate-300 hover:text-white px-2 py-0.5 rounded-lg bg-slate-800 border border-slate-700 transition-colors inline-flex items-center gap-1"
                    title="Copy Email ID"
                  >
                    {copiedEmail ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                    <span>{copiedEmail ? 'Copied!' : 'Copy'}</span>
                  </button>
                </div>
                
                <a
                  href={`mailto:${officialEmail}?subject=Freelance%20Career%20Inquiry%20-%20ASAP%20Learning`}
                  className="text-xs font-bold text-emerald-400 hover:underline block break-all"
                >
                  {officialEmail}
                </a>

                <p className="text-[10px] text-slate-400">
                  Click to launch your email client with pre-filled details.
                </p>
              </div>
            </div>

            {/* Category Application Volume Live Indicator */}
            <div className="mt-4 p-3.5 rounded-2xl bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 border border-blue-200/80 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-blue-600 text-white shrink-0">
                  <BarChart3 className="w-4 h-4" />
                </div>
                <div>
                  <span className="font-extrabold text-slate-900 block">
                    Category Application Volume: <span className="text-blue-700">{formData.appliedRole}</span>
                  </span>
                  <span className="text-slate-600 text-[11px]">
                    <strong className="text-blue-700 font-bold">{categoryStats.find(s => s.department === (CAREER_ROLES.find(r => r.title === formData.appliedRole)?.department || ''))?.applicationCount || 0} candidate(s)</strong> have applied in this category.
                  </span>
                </div>
              </div>

              <div className="px-3 py-1.5 rounded-xl bg-white border border-blue-200 text-slate-800 font-bold text-[11px] shrink-0 flex items-center gap-1.5 shadow-2xs">
                <Users className="w-3.5 h-3.5 text-blue-600" />
                <span>{totalApplications} Total Applicants</span>
              </div>
            </div>
          </div>

          {isSubmitted ? (
            /* Success State Confirmation Box */
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-slate-900 text-white rounded-2xl p-8 sm:p-12 text-center space-y-6"
            >
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-10 h-10" />
              </div>

              <div className="space-y-2">
                <span className="px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 text-xs font-mono font-bold tracking-wider">
                  Reference ID: {applicationId}
                </span>
                <h3 className="text-2xl sm:text-3xl font-bold text-white">
                  Application Received Successfully!
                </h3>
                <p className="text-slate-300 text-sm max-w-lg mx-auto">
                  Thank you, <strong className="text-white">{formData.fullName}</strong>. We have received your application and resume for the <strong className="text-blue-400">{formData.appliedRole}</strong> position.
                </p>
              </div>

              <div className="p-4 rounded-xl bg-slate-800/80 border border-slate-700/80 max-w-md mx-auto text-xs text-slate-300 text-left space-y-2">
                <p className="font-bold text-white flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-emerald-400" /> What happens next?
                </p>
                <p>• Our publishing team will evaluate your skill profile against current project batches.</p>
                <p>• Workstation setup recorded: <strong>{formData.ownSystem} System</strong> • <strong>{formData.internetType}</strong> • Power Backup: <strong>{formData.powerBackup}</strong>.</p>
                <p>• If selected, you will receive a test sample layout or interview invitation at <strong>{formData.email}</strong>.</p>
                <p>• Response time is typically within 1-2 business days.</p>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
                <a
                  href={constructMailtoUrl()}
                  className="px-6 py-3 rounded-xl text-xs font-bold bg-blue-600 text-white hover:bg-blue-500 transition-colors inline-flex items-center gap-2 shadow-lg shadow-blue-600/30"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Mail className="w-4 h-4" />
                  <span>Launch Email Client ({officialEmail})</span>
                </a>

                <a
                  href={constructWhatsappUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 rounded-xl text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-500 transition-colors inline-flex items-center gap-2 shadow-lg shadow-emerald-600/30"
                >
                  <MessageSquare className="w-4 h-4" />
                  <span>Send Details via WhatsApp HR (+91 7817899559)</span>
                </a>

                <button
                  onClick={() => {
                    setIsSubmitted(false);
                    setFormData({
                      fullName: '',
                      email: '',
                      phone: '',
                      location: '',
                      appliedRole: CAREER_ROLES[0].title,
                      experienceYears: '3-5 years',
                      skills: ['Adobe InDesign CC'],
                      availability: '20-40 hours / week',
                      expectedRate: '',
                      ownSystem: 'Windows',
                      internetType: 'WIFI Connection',
                      powerBackup: 'Yes',
                      portfolioUrl: '',
                      linkedinUrl: '',
                      resumeDriveUrl: '',
                      coverLetter: '',
                    });
                  }}
                  className="px-6 py-3 rounded-xl text-xs font-bold bg-slate-800 text-slate-300 hover:text-white hover:bg-slate-700 transition-colors inline-flex items-center gap-2 border border-slate-700"
                >
                  <span>Submit Another Application</span>
                </button>
              </div>
            </motion.div>
          ) : (
            /* Application Input Form */
            <form onSubmit={handleSubmit} className="space-y-8">
              
              {formError && (
                <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-medium flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                  <span>{formError}</span>
                </div>
              )}

              {/* Step 1: Role Selection & Personal Contact */}
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-2">
                  <User className="w-4 h-4 text-blue-600" /> 1. Personal & Contact Details
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                  {/* Row 1 */}
                  <div>
                    <label htmlFor="career-target-role" className="block text-xs font-bold text-slate-700 mb-1 flex items-center gap-1.5 h-5">
                      Target Role / Position <span className="text-rose-500">*</span>
                    </label>
                    <select
                      id="career-target-role"
                      value={formData.appliedRole}
                      onChange={(e) => setFormData({ ...formData, appliedRole: e.target.value })}
                      className="w-full h-10 px-3.5 py-2 rounded-xl border border-slate-300 text-slate-900 text-sm font-semibold bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    >
                      {CAREER_ROLES.map((role) => (
                        <option key={role.id} value={role.title}>
                          {role.title} ({role.type})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="career-full-name" className="block text-xs font-bold text-slate-700 mb-1 flex items-center gap-1.5 h-5">
                      Full Name <span className="text-rose-500">*</span>
                    </label>
                    <input
                      id="career-full-name"
                      type="text"
                      required
                      placeholder="e.g. Rahul Sharma"
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      className="w-full h-10 px-3.5 py-2 rounded-xl border border-slate-300 text-slate-900 text-sm focus:ring-2 focus:ring-blue-500 bg-white transition-all"
                    />
                  </div>

                  {/* Row 2 */}
                  <div>
                    <label htmlFor="career-email" className="block text-xs font-bold text-slate-700 mb-1 flex items-center gap-1.5 h-5">
                      Email Address <span className="text-rose-500">*</span>
                    </label>
                    <input
                      id="career-email"
                      type="email"
                      required
                      placeholder="e.g. rahul@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full h-10 px-3.5 py-2 rounded-xl border border-slate-300 text-slate-900 text-sm focus:ring-2 focus:ring-blue-500 bg-white transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor="career-phone" className="block text-xs font-bold text-slate-700 mb-1 flex items-center gap-1.5 h-5">
                      Phone / WhatsApp Number <span className="text-rose-500">*</span>
                    </label>
                    <input
                      id="career-phone"
                      type="tel"
                      required
                      placeholder="+91 98765 43210"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full h-10 px-3.5 py-2 rounded-xl border border-slate-300 text-slate-900 text-sm focus:ring-2 focus:ring-blue-500 bg-white transition-all"
                    />
                  </div>

                  {/* Row 3 */}
                  <div>
                    <label htmlFor="career-location" className="block text-xs font-bold text-slate-700 mb-1 flex items-center gap-1.5 h-5">
                      Current City & Country <span className="text-rose-500">*</span>
                    </label>
                    <input
                      id="career-location"
                      type="text"
                      required
                      placeholder="e.g. New Delhi, India / Remote"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="w-full h-10 px-3.5 py-2 rounded-xl border border-slate-300 text-slate-900 text-sm focus:ring-2 focus:ring-blue-500 bg-white transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1 flex items-center gap-1.5 h-5">
                      <Building className="w-3.5 h-3.5 text-indigo-600" /> Preferred Work Mode <span className="text-rose-500">*</span>
                    </label>
                    <div className="grid grid-cols-3 gap-1 h-10">
                      {[
                        { id: 'Remote', label: 'Remote' },
                        { id: 'Hybrid', label: 'Hybrid' },
                        { id: 'On-Site', label: 'On-Site' }
                      ].map((mode) => {
                        const isSelected = (formData.preferredWorkMode || 'Remote') === mode.id;
                        return (
                          <button
                            type="button"
                            key={mode.id}
                            onClick={() => setFormData({ ...formData, preferredWorkMode: mode.id })}
                            className={`h-10 px-1 rounded-xl text-xs font-bold transition-all border text-center flex items-center justify-center ${
                              isSelected
                                ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
                            }`}
                          >
                            {mode.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Row 4 */}
                  <div>
                    <label htmlFor="career-aadhaar" className="block text-xs font-bold text-slate-700 mb-1 flex items-center gap-1.5 h-5">
                      <CreditCard className="w-3.5 h-3.5 text-blue-600" /> Aadhaar Card Number <span className="text-rose-500">*</span>
                    </label>
                    <input
                      id="career-aadhaar"
                      type="text"
                      required
                      maxLength={14}
                      placeholder="12-digit Aadhaar (e.g. 1234 5678 9012)"
                      value={formData.aadhaarNumber || ''}
                      onChange={(e) => setFormData({ ...formData, aadhaarNumber: e.target.value })}
                      className="w-full h-10 px-3.5 py-2 rounded-xl border border-slate-300 text-slate-900 text-sm focus:ring-2 focus:ring-blue-500 bg-white transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor="career-pan" className="block text-xs font-bold text-slate-700 mb-1 flex items-center gap-1.5 h-5">
                      <CreditCard className="w-3.5 h-3.5 text-purple-600" /> PAN Card Number <span className="text-rose-500">*</span>
                    </label>
                    <input
                      id="career-pan"
                      type="text"
                      required
                      maxLength={10}
                      placeholder="10-character PAN (e.g. ABCDE1234F)"
                      value={formData.panNumber || ''}
                      onChange={(e) => setFormData({ ...formData, panNumber: e.target.value.toUpperCase() })}
                      className="w-full h-10 px-3.5 py-2 rounded-xl border border-slate-300 text-slate-900 text-sm uppercase focus:ring-2 focus:ring-blue-500 bg-white transition-all"
                    />
                  </div>

                  {/* Row 5 */}
                  <div>
                    <label htmlFor="career-experience" className="block text-xs font-bold text-slate-700 mb-1 flex items-center gap-1.5 h-5">
                      Relevant Experience <span className="text-rose-500">*</span>
                    </label>
                    <select
                      id="career-experience"
                      required
                      value={formData.experienceYears}
                      onChange={(e) => setFormData({ ...formData, experienceYears: e.target.value })}
                      className="w-full h-10 px-3.5 py-2 rounded-xl border border-slate-300 text-slate-900 text-sm font-semibold bg-white focus:ring-2 focus:ring-blue-500 transition-all"
                    >
                      <option value="1-2 years">1 - 2 Years</option>
                      <option value="3-5 years">3 - 5 Years</option>
                      <option value="5-10 years">5 - 10 Years</option>
                      <option value="10+ years">10+ Years (Senior Master)</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="career-address" className="block text-xs font-bold text-slate-700 mb-1 flex items-center gap-1.5 h-5">
                      <MapPin className="w-3.5 h-3.5 text-blue-600" /> Communication Address <span className="text-rose-500">*</span>
                    </label>
                    <input
                      id="career-address"
                      type="text"
                      required
                      placeholder="House No., Street, City, State, PIN Code"
                      value={formData.communicationAddress || ''}
                      onChange={(e) => setFormData({ ...formData, communicationAddress: e.target.value })}
                      className="w-full h-10 px-3.5 py-2 rounded-xl border border-slate-300 text-slate-900 text-sm focus:ring-2 focus:ring-blue-500 bg-white transition-all"
                    />
                  </div>
                </div>
              </div>

              {/* Step 2: Rate, Availability & Skills */}
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-2">
                  <Briefcase className="w-4 h-4 text-purple-600" /> 2. Availability, Rate & Skill Set
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="career-availability" className="block text-xs font-bold text-slate-700 mb-1">
                      Weekly Availability <span className="text-rose-500">*</span>
                    </label>
                    <select
                      id="career-availability"
                      required
                      value={formData.availability}
                      onChange={(e) => setFormData({ ...formData, availability: e.target.value })}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-slate-900 text-sm font-semibold bg-white focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="10-20 hours / week">10 - 20 Hours / Week (Part-time Freelance)</option>
                      <option value="20-40 hours / week">20 - 40 Hours / Week (Active Freelance)</option>
                      <option value="30-40 hours / week">30 - 40 Hours / Week (Dedicated Freelance)</option>
                      <option value="Project-based On Demand">Project-Based On Demand</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="career-rate" className="block text-xs font-bold text-slate-700 mb-1">
                      Expected Rate / Compensation <span className="text-rose-500">*</span>
                    </label>
                    <input
                      id="career-rate"
                      type="text"
                      required
                      placeholder="e.g. As per Retainer + Commission OR Page-Wise / Project-Wise SLA"
                      value={formData.expectedRate}
                      onChange={(e) => setFormData({ ...formData, expectedRate: e.target.value })}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-slate-900 text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="career-portfolio" className="block text-xs font-bold text-slate-700 mb-1">
                      Portfolio / Google Drive / Sample Work Link <span className="text-rose-500">*</span>
                    </label>
                    <input
                      id="career-portfolio"
                      type="url"
                      required
                      placeholder="https://drive.google.com/... or Behance link"
                      value={formData.portfolioUrl}
                      onChange={(e) => setFormData({ ...formData, portfolioUrl: e.target.value })}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-slate-900 text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="career-linkedin" className="block text-xs font-bold text-slate-700 mb-1">
                      LinkedIn Profile URL <span className="text-rose-500">*</span>
                    </label>
                    <input
                      id="career-linkedin"
                      type="url"
                      required
                      placeholder="https://linkedin.com/in/yourname"
                      value={formData.linkedinUrl}
                      onChange={(e) => setFormData({ ...formData, linkedinUrl: e.target.value })}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-slate-900 text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Primary Skills Selection */}
                <div className="pt-2">
                  <label className="block text-xs font-bold text-slate-700 mb-2 flex items-center justify-between">
                    <span>Primary Software & Skill Proficiency (Select all that apply)</span>
                    <span className="text-rose-600 font-extrabold text-[11px]">* Microsoft Office is Mandatory</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {AVAILABLE_SKILLS.map((skill) => {
                      const isSelected = formData.skills.includes(skill);
                      const isMandatory = skill.includes('Microsoft Office');
                      return (
                        <button
                          type="button"
                          key={skill}
                          onClick={() => toggleSkill(skill)}
                          className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all flex items-center gap-1.5 ${
                            isSelected
                              ? isMandatory
                                ? 'bg-indigo-700 text-white shadow-xs border border-indigo-600'
                                : 'bg-blue-600 text-white shadow-xs'
                              : isMandatory
                                ? 'bg-rose-50 text-rose-700 border-2 border-rose-300 hover:bg-rose-100 font-bold'
                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200'
                          }`}
                        >
                          {isSelected && <Check className="w-3.5 h-3.5" />}
                          <span>{skill}</span>
                          {isMandatory && (
                            <span className={`text-[10px] uppercase tracking-wider font-extrabold px-1.5 py-0.5 rounded-md ${
                              isSelected ? 'bg-amber-300 text-slate-900' : 'bg-rose-600 text-white'
                            }`}>
                              Mandatory
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Step 3: Workstation & Hardware Infrastructure */}
              <div className="space-y-4 pt-2 border-t border-slate-100">
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-2">
                  <Laptop className="w-4 h-4 text-indigo-600" /> 3. Workstation & Technical Setup
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Field 1: Do you have your own system (Windows / MAC)? */}
                  <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-2">
                    <label className="block text-xs font-extrabold text-slate-800 flex items-center gap-1.5">
                      <Laptop className="w-3.5 h-3.5 text-blue-600" /> Own System / Computer? <span className="text-rose-500">*</span>
                    </label>
                    <p className="text-[11px] text-slate-500">System OS platform (Required)</p>
                    <div className="grid grid-cols-2 gap-1.5 pt-1">
                      {[
                        { id: 'Windows', label: 'Windows' },
                        { id: 'MAC', label: 'MAC' },
                        { id: 'Both', label: 'Both' },
                        { id: 'None', label: 'None (No System)' }
                      ].map((sys) => {
                        const isSelected = formData.ownSystem === sys.id;
                        return (
                          <button
                            type="button"
                            key={sys.id}
                            onClick={() => setFormData({ ...formData, ownSystem: sys.id })}
                            className={`py-2 px-2 rounded-xl text-[11px] font-bold transition-all text-center border ${
                              isSelected
                                ? sys.id === 'None'
                                  ? 'bg-rose-600 text-white border-rose-600 shadow-xs'
                                  : 'bg-blue-600 text-white border-blue-600 shadow-xs'
                                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
                            }`}
                          >
                            {sys.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Field 2: Stable Internet Connection */}
                  <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-2">
                    <label className="block text-xs font-extrabold text-slate-800 flex items-center gap-1.5">
                      <Wifi className="w-3.5 h-3.5 text-emerald-600" /> Stable Internet Connection <span className="text-rose-500">*</span>
                    </label>
                    <p className="text-[11px] text-slate-500">Internet connection type (Required)</p>
                    <div className="space-y-1.5 pt-1">
                      {[
                        { value: 'WIFI Connection', label: 'WIFI Connection' },
                        { value: 'Mobile Data', label: 'Mobile Data' },
                        { value: 'Both (WIFI & Mobile)', label: 'Both (WIFI & Mobile Data)' },
                        { value: 'No Internet Connection', label: '❌ No Internet Connection' }
                      ].map((net) => {
                        const isSelected = formData.internetType === net.value;
                        return (
                          <button
                            type="button"
                            key={net.value}
                            onClick={() => setFormData({ ...formData, internetType: net.value })}
                            className={`w-full py-1.5 px-2.5 rounded-xl text-xs font-bold transition-all text-left border flex items-center justify-between ${
                              isSelected
                                ? net.value === 'No Internet Connection'
                                  ? 'bg-rose-600 text-white border-rose-600 shadow-xs'
                                  : 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
                            }`}
                          >
                            <span className="truncate">{net.label}</span>
                            {isSelected && <Check className="w-3.5 h-3.5 shrink-0" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Field 3: Power Backup */}
                  <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-2">
                    <label className="block text-xs font-extrabold text-slate-800 flex items-center gap-1.5">
                      <Zap className="w-3.5 h-3.5 text-amber-500" /> Power Backup (UPS/Inverter) <span className="text-rose-500">*</span>
                    </label>
                    <p className="text-[11px] text-slate-500">Continuous power availability</p>
                    <div className="grid grid-cols-2 gap-2 pt-2">
                      {['Yes', 'No'].map((opt) => {
                        const isSelected = formData.powerBackup === opt;
                        return (
                          <button
                            type="button"
                            key={opt}
                            onClick={() => setFormData({ ...formData, powerBackup: opt })}
                            className={`py-3 px-3 rounded-xl text-xs font-bold transition-all text-center border ${
                              isSelected
                                ? opt === 'Yes'
                                  ? 'bg-amber-500 text-white border-amber-500 shadow-xs'
                                  : 'bg-rose-500 text-white border-rose-500 shadow-xs'
                                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
                            }`}
                          >
                            {opt === 'Yes' ? '⚡ Yes' : '❌ No'}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* System & Internet Mandatory Warning Banner */}
                {(formData.ownSystem === 'None' || formData.internetType === 'No Internet Connection' || formData.internetType === 'None') && (
                  <div className="p-4 rounded-2xl bg-rose-50 border border-rose-300 text-rose-900 text-xs font-semibold flex items-start gap-3 shadow-xs">
                    <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="font-extrabold text-rose-800 text-sm">
                        🚫 Application Submission Blocked
                      </p>
                      <p className="text-rose-700 text-xs leading-relaxed">
                        To work as a freelance typesetter, XML editor, or accessibility specialist at <strong>ASAP LEARNING</strong>, candidates must possess a working computer system (Windows or MAC) and an active internet connection. Applications without a computer system or internet connection cannot be filled or submitted.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Step 4: Resume Google Drive / Cloud Link Input */}
              <div className="space-y-3">
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-2">
                  <FileText className="w-4 h-4 text-emerald-600" /> 4. Resume / CV Drive Link (Paste Link Only) <span className="text-rose-500">*</span>
                </h3>

                <div className="p-4 rounded-2xl bg-blue-50/70 border border-blue-200 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-xl bg-blue-600 text-white shrink-0 mt-0.5">
                      <LinkIcon className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-blue-900">
                        File upload on this page is disabled. Please paste a cloud link to your Resume / CV below.
                      </p>
                      <p className="text-[11px] text-blue-700 mt-0.5">
                        Accepts Google Drive, OneDrive, Dropbox, or public cloud storage links. Ensure sharing permission is set to <strong>"Anyone with the link can view"</strong>.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center gap-2 pt-1">
                    <div className="relative flex-1 w-full">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                        <Globe className="w-4 h-4" />
                      </div>
                      <input
                        id="career-resume-drive-url"
                        type="url"
                        required
                        placeholder="https://drive.google.com/file/d/... or OneDrive / Dropbox link"
                        aria-label="Resume or CV Google Drive URL link"
                        value={formData.resumeDriveUrl}
                        onChange={(e) => setFormData({ ...formData, resumeDriveUrl: e.target.value })}
                        className="w-full pl-10 pr-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-sm font-medium focus:ring-2 focus:ring-blue-500 shadow-xs"
                      />
                    </div>

                    {formData.resumeDriveUrl.trim().length > 5 && (
                      <a
                        href={formData.resumeDriveUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-white text-blue-700 hover:bg-blue-100 border border-blue-300 font-bold text-xs transition-colors shrink-0 flex items-center justify-center gap-1.5"
                      >
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        <span>Test Link</span>
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* Step 5: Cover Letter / Brief Bio */}
              <div className="space-y-2">
                <label htmlFor="career-cover-letter" className="block text-xs font-bold text-slate-700">
                  Brief Intro / Experience Highlights <span className="text-rose-500">*</span>
                </label>
                <textarea
                  id="career-cover-letter"
                  rows={3}
                  required
                  placeholder="Tell us briefly about your typesetting, editorial, or technical publishing experience..."
                  value={formData.coverLetter}
                  onChange={(e) => setFormData({ ...formData, coverLetter: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-slate-900 text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Submit Buttons */}
              <div className="pt-2 space-y-3">
                {formData.ownSystem === 'None' || formData.internetType === 'No Internet Connection' || formData.internetType === 'None' ? (
                  <div className="w-full py-4 px-6 rounded-2xl font-bold text-sm text-rose-800 bg-rose-100 border border-rose-300 flex items-center justify-center gap-2 shadow-xs">
                    <AlertCircle className="w-5 h-5 text-rose-600 shrink-0" />
                    <span>Cannot Submit: Personal System & Internet Connection Required</span>
                  </div>
                ) : (
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 rounded-2xl font-bold text-base text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg shadow-blue-600/25 transition-all inline-flex items-center justify-center gap-2 disabled:opacity-60 cursor-pointer"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Submitting Application & Resume...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>Submit Freelancer Application Online</span>
                      </>
                    )}
                  </button>
                )}

                <div className="relative flex py-1 items-center">
                  <div className="flex-grow border-t border-slate-200"></div>
                  <span className="flex-shrink mx-4 text-xs font-semibold text-slate-400">OR EMAIL DIRECTLY</span>
                  <div className="flex-grow border-t border-slate-200"></div>
                </div>

                <a
                  href={constructMailtoUrl()}
                  className="w-full py-3.5 rounded-2xl font-bold text-sm text-slate-800 bg-slate-100 hover:bg-slate-200 border border-slate-300 transition-all inline-flex items-center justify-center gap-2"
                >
                  <Mail className="w-4 h-4 text-blue-600" />
                  <span>Send Application via Email App ({officialEmail})</span>
                </a>
              </div>

            </form>
          )}

        </div>

      </section>

    </div>
  );
};
